"use client";

// eslint-disable-next-line no-unused-vars
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, ChevronUp, Menu, User2, X } from "lucide-react";
import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import CentreArea from "../components/CentreArea";
import LeftSidebar from "../components/LeftSidebar";
import AlertModal from "../components/AlertModal";
import { fetchAllConversationApi } from "../../services/conversationApi";
import { baseUrl } from "../../services/axios";
import { axiosAuth } from "../../services/axiosAuth";
import { fetchAllMessagesApi } from "../../services/messageApi";
import { fetchCustomModels } from "../../services/modelApi";
import { toast } from "react-toastify";
import { useAuth } from "../context/AuthContext";

const Dashboard = () => {
  const { chatId: urlChatId } = useParams();
  const { userData, isAuthenticated, fetchUserData } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectedChatId, setSelectedChatId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [isNewChat, setIsNewChat] = useState(true);
  const [refreshSidebar, setRefreshSidebar] = useState(0);
  const [chatTitles, setChatTitles] = useState({});
  const [, setMobileMenuOpen] = useState(false);
  const [mobileView, setMobileView] = useState("main");
  const [windowWidth, setWindowWidth] = useState(() => {
    if (typeof window !== "undefined") {
      return window.innerWidth;
    }
    return 1024; // Default to desktop width for SSR
  });
  const [selectedChatCompleted] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [modalTitle, setModalTitle] = useState("");
  const [showContactAdmin, setShowContactAdmin] = useState(false);
  const [tierCode, setTierCode] = useState(null);
  const [chats, setChats] = useState([]);
  const [allChats, setAllChats] = useState([]);

  const [latestUserData, setLatestUserData] = useState(null);
  const [activeClientLabel, setActiveClientLabel] = useState(null);
  const [isSwitchingClient, setIsSwitchingClient] = useState(false);
  const [isChatStreaming, setIsChatStreaming] = useState(false);
  const [clientMismatchPopupMinimized, setClientMismatchPopupMinimized] =
    useState(false);
  const [portalClientLabel, setPortalClientLabel] = useState(null);

  const refreshLatestUserAndClients = useCallback(
    async (toolUserSnapshot = null) => {
      if (!isAuthenticated) {
        setLatestUserData(null);
        setActiveClientLabel(null);
        setPortalClientLabel(null);
        return;
      }
      try {
        const [userRes, clientsRes] = await Promise.all([
          axiosAuth.get("/user"),
          axiosAuth.get("/v2/clients"),
        ]);
        if (userRes.data) setLatestUserData(userRes.data);

        const clients = clientsRes.data?.clients ?? [];
        const toolClientId =
          toolUserSnapshot?.activeClientId ?? userData?.activeClientId;
        const match = toolClientId
          ? clients.find((c) => String(c.id) === String(toolClientId))
          : null;
        setActiveClientLabel(match?.name ?? null);
        const portalClientId = userRes.data?.activeClientId;
        const portalMatch = portalClientId
          ? clients.find((c) => String(c.id) === String(portalClientId))
          : null;
        setPortalClientLabel(portalMatch?.name ?? null);
      } catch {
        setLatestUserData(null);
        setActiveClientLabel(null);
        setPortalClientLabel(null);
      }
    },
    [isAuthenticated, userData?.activeClientId],
  );

  useEffect(() => {
    void refreshLatestUserAndClients();
  }, [refreshLatestUserAndClients]);

  useEffect(() => {
    if (!isAuthenticated) return undefined;

    const run = () => {
      void refreshLatestUserAndClients();
    };

    const onVisible = () => {
      if (document.visibilityState === "visible") run();
    };

    const onWindowFocus = () => {
      if (document.visibilityState === "visible") run();
    };

    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onWindowFocus);

    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onWindowFocus);
    };
  }, [isAuthenticated, refreshLatestUserAndClients]);

  const sessionClientBadgeLabel =
    activeClientLabel ??
    (userData?.isPrimaryUser ? "Primary Client" : "Client");

  const clientSessionMismatch = Boolean(
    isAuthenticated &&
    userData?.activeClientId &&
    latestUserData?.activeClientId &&
    String(userData.activeClientId) !== String(latestUserData.activeClientId),
  );

  useEffect(() => {
    if (clientSessionMismatch) {
      setClientMismatchPopupMinimized(false);
    }
  }, [clientSessionMismatch]);

  // Initialize model selection - don't auto-load from localStorage
  const [customModelId, setCustomModelId] = useState(undefined);

  const [allCustomModels, setAllCustomModels] = useState([]);
  const [selectedModel] = useState(null);
  const centerRef = useRef();

  // Wrapper function to save model selection to localStorage
  const updateCustomModelId = useCallback((newModel) => {
    setCustomModelId(newModel);
    if (newModel) {
      localStorage.setItem("selectedCustomModel", JSON.stringify(newModel));
    } else {
      localStorage.removeItem("selectedCustomModel");
    }
  }, []);

  const fetchModels = useCallback(async () => {
    try {
      const res = await fetchCustomModels();
      const modelOptions = res.data.map((model) => ({
        id: model._id,
        label: model.name,
        value: model.modelHandle,
        folder: model.folder || null,
        greeting: model.greeting || null,
        conversationStarters: model.conversationStarters || null,
      }));
      // Removed localStorage auto-selection logic
      // Only select model from URL params or user click

      setAllCustomModels(modelOptions);

      // Don't auto-select first model - only select from URL params
      // Model selection should only happen when user clicks or URL has botId

      return modelOptions;
    } catch (error) {
      toast.error(error.message || "Failed to fetch models");
    }
  }, []);

  // Handle screen resize to detect mobile
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setWindowWidth(width);
      if (width >= 1024) {
        setMobileMenuOpen(false);
        setMobileView("main");
      }
    };

    const handleToggleLeftSidebar = () => {
      setMobileView("left");
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("toggleLeftSidebar", handleToggleLeftSidebar);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("toggleLeftSidebar", handleToggleLeftSidebar);
    };
  }, []);

  const fetchConversations = useCallback(
    async (overrideClientId) => {
      const clientId = overrideClientId ?? userData?.activeClientId;
      if (!clientId) {
        return;
      }
      try {
        const res = await fetchAllConversationApi({ clientId });
        setChats(
          res.data.conversations
            .filter((convo) => convo.usedCustomModel)
            .map((convo) => ({
              _id: convo._id,
              title: convo.conversationName,
              createdAt: convo.createdAt,
              modelHandle: convo.usedCustomModel,
              folderId: convo.folderId,
            })),
        );
        setAllChats(
          res.data.conversations
            .filter((convo) => convo.usedCustomModel)
            .map((convo) => ({
              _id: convo._id,
              title: convo.conversationName,
              createdAt: convo.createdAt,
              modelHandle: convo.usedCustomModel,
              folderId: convo.folderId,
            })),
        );
      } catch (error) {
        console.error("Failed to fetch conversations:", error);
        toast.error("Failed to fetch conversations");
      }
    },
    [userData?.activeClientId],
  );

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations, userData?.activeClientId]);

  const handleSwitchToPortalClient = useCallback(async () => {
    const targetId = latestUserData?.activeClientId;
    if (!targetId || !userData?.activeClientId) return;
    if (String(targetId) === String(userData.activeClientId)) return;

    setIsSwitchingClient(true);
    try {
      await axiosAuth.post("/v2/clients/set-active", { clientId: targetId });

      setMessages([]);
      setSelectedChatId(null);
      setIsNewChat(true);
      setChats([]);
      setAllChats([]);
      navigate("/dashboard", { replace: true });

      const portalData = await fetchUserData();
      await refreshLatestUserAndClients(portalData ?? null);
      // Always refetch using the known switched-to client id.
      // `portalData` can be stale/partial depending on API shape.
      await fetchConversations(targetId);
    } catch (err) {
      const msg =
        err.response?.data?.msg ||
        err.response?.data?.message ||
        "Could not switch client. Try again from the portal.";
      toast.error(msg, { autoClose: 5000 });
    } finally {
      setIsSwitchingClient(false);
      setPortalClientLabel(null);
    }
  }, [
    latestUserData?.activeClientId,
    userData?.activeClientId,
    fetchUserData,
    refreshLatestUserAndClients,
    fetchConversations,
    navigate,
  ]);

  // Restore selected bot and chat from URL parameters ONLY
  // Don't auto-select if URL is empty
  useEffect(() => {
    const botIdFromUrl = searchParams.get("botId");
    const chatIdFromUrl = searchParams.get("chatId") || urlChatId;

    // Only restore bot selection if botId is explicitly in URL
    if (botIdFromUrl && allCustomModels.length > 0) {
      const botFromUrl = allCustomModels.find(
        (model) => model.id === botIdFromUrl,
      );
      if (botFromUrl && (!customModelId || customModelId.id !== botIdFromUrl)) {
        updateCustomModelId(botFromUrl);
        userHasManuallySelectedModel.current = true;

        // Filter chats for this bot
        const filteredChat = allChats.filter(
          (chat) => chat.modelHandle?._id === botIdFromUrl,
        );
        setChats(filteredChat);
      }
    }
    // If no botId in URL, don't auto-select - let user choose

    // Restore chat selection from URL
    if (chatIdFromUrl && chats.length > 0) {
      const chatFromUrl = chats.find((chat) => chat._id === chatIdFromUrl);
      if (
        chatFromUrl &&
        (!selectedChatId || selectedChatId._id !== chatIdFromUrl)
      ) {
        setSelectedChatId(chatFromUrl);
        setIsNewChat(false);
      }
    }
  }, [
    urlChatId,
    searchParams,
    chats,
    allChats,
    allCustomModels,
    customModelId,
    selectedChatId,
    updateCustomModelId,
  ]);

  // Fetch models only once on mount
  useEffect(() => {
    fetchModels();
  }, [fetchModels]);

  // Removed localStorage auto-restoration - only restore from URL params
  // This prevents auto-selection when URL is empty
  // Track whether user has manually changed the model to prevent auto-override
  const userHasManuallySelectedModel = useRef(false);

  // Create a special wrapper that marks manual model selection
  const handleManualModelChange = useCallback(
    (newModel) => {
      const filteredChat = allChats.filter(
        (chat) => chat.modelHandle?._id == newModel.id,
      );

      // Update URL with botId parameter
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set("botId", newModel.id);

      // Don't auto-select the first chat - just filter and update model
      // Only clear selection if switching to a bot with no chats
      if (filteredChat.length === 0) {
        console.log("id not setting");
        newSearchParams.delete("chatId");
        navigate(`/dashboard?${newSearchParams.toString()}`, { replace: true });
        setMessages([]);
        setIsNewChat(true);
        setSelectedChatId(null);
      } else {
        // If there are chats but the currently selected chat doesn't belong to this model, clear selection
        if (selectedChatId && selectedChatId.modelHandle?._id !== newModel.id) {
          newSearchParams.delete("chatId");
          navigate(`/dashboard?${newSearchParams.toString()}`, {
            replace: true,
          });
          setSelectedChatId(null);
          setIsNewChat(true);
          setMessages([]);
        } else {
          // Keep chatId if it exists and belongs to this model
          if (
            selectedChatId &&
            selectedChatId.modelHandle?._id === newModel.id
          ) {
            newSearchParams.set("chatId", selectedChatId._id);
          }
          navigate(
            `/dashboard${
              selectedChatId && selectedChatId.modelHandle?._id === newModel.id
                ? `/${selectedChatId._id}`
                : ""
            }?${newSearchParams.toString()}`,
            { replace: true },
          );
        }
      }

      setChats(filteredChat);
      userHasManuallySelectedModel.current = true;
      updateCustomModelId(newModel);
    },
    [allChats, navigate, updateCustomModelId, selectedChatId, searchParams],
  );
  const handleManualModelChangeCenterArea = useCallback(
    (newModel) => {
      setChats(allChats);
      userHasManuallySelectedModel.current = true;
      updateCustomModelId(newModel);
    },
    [allChats, updateCustomModelId],
  );

  // Only auto-select chat's model if user hasn't manually selected a different one
  useEffect(() => {
    if (
      selectedChatId &&
      selectedChatId.modelHandle &&
      allCustomModels.length > 0 &&
      !userHasManuallySelectedModel.current
    ) {
      // Only auto-select if no model is selected or if it's different from the chat's model
      const chatModelId = selectedChatId.modelHandle._id;
      const currentModelId = customModelId?.id;

      if (!currentModelId || currentModelId !== chatModelId) {
        updateCustomModelId({
          id: selectedChatId.modelHandle._id,
          label: selectedChatId.modelHandle.name,
          value: selectedChatId.modelHandle.modelHandle,
        });
      }
    }
  }, [selectedChatId, allCustomModels, updateCustomModelId, customModelId]);

  const fetchMessages = useCallback(async () => {
    if (!selectedChatId) {
      return;
    }
    setFetching(true);
    try {
      const res = await fetchAllMessagesApi(selectedChatId._id);
      const apiOrigin = (baseUrl || "").replace(/\/?api$/, "");
      setMessages(
        res.data.messages.map((msg) => {
          const isImage = msg.messageType === "image";
          const isPdf = msg.messageType === "pdf";
          const isDocx = msg.messageType === "docx";
          const isCsv = msg.messageType === "csv";

          let imageUrl = null;
          let pdfUrl = null;
          let docxUrl = null;
          let csvUrl = null;
          if (isImage) {
            if (msg.directoryPath && /^https?:\/\//i.test(msg.directoryPath)) {
              imageUrl = msg.directoryPath;
            } else if (msg.media) {
              const path = msg.media.startsWith("/")
                ? msg.media
                : `/${msg.media}`;
              imageUrl = `${apiOrigin}${path}`;
            }
          }
          if (isPdf) {
            if (msg.directoryPath && /^https?:\/\//i.test(msg.directoryPath)) {
              pdfUrl = msg.directoryPath;
            } else if (msg.media) {
              const path = msg.media.startsWith("/")
                ? msg.media
                : `/${msg.media}`;
              pdfUrl = `${apiOrigin}${path}`;
            }
          }
          if (isDocx) {
            if (msg.directoryPath && /^https?:\/\//i.test(msg.directoryPath)) {
              docxUrl = msg.directoryPath;
            } else if (msg.media) {
              const path = msg.media.startsWith("/")
                ? msg.media
                : `/${msg.media}`;
              docxUrl = `${apiOrigin}${path}`;
            }
          }
          if (isCsv) {
            if (msg.directoryPath && /^https?:\/\//i.test(msg.directoryPath)) {
              csvUrl = msg.directoryPath;
            } else if (msg.media) {
              const path = msg.media.startsWith("/")
                ? msg.media
                : `/${msg.media}`;
              csvUrl = `${apiOrigin}${path}`;
            }
          }

          return {
            id: msg._id,
            role: msg.sender,
            text: isImage ? null : msg.content,
            type: msg.messageType || "text",
            image: imageUrl,
            involvedUser: msg?.involvedUser?.name,
            timestamp: msg.createdAt,
            pdfFile: pdfUrl,
            docxFile: docxUrl,
            csvFile: csvUrl,
          };
        }),
      );
    } catch (error) {
      toast.error(error.message || "Failed to fetch messages");
    } finally {
      setFetching(false);
    }
  }, [selectedChatId]);

  useEffect(() => {
    fetchMessages();
  }, [selectedChatId, fetchMessages]);

  const sidebarRef = useRef();

  const handleChatSelect = useCallback(
    (chatId, isNew = false) => {
      setSelectedChatId(chatId);
      setIsNewChat(isNew);

      // Reset manual selection flag when selecting a different chat
      // This allows auto-selection of the chat's model
      if (chatId && !isNew) {
        userHasManuallySelectedModel.current = false;
      }

      // Update URL with both chatId (path) and chatId (query param) and botId
      const newSearchParams = new URLSearchParams(searchParams);

      if (chatId && !isNew) {
        // Add chatId to query params
        newSearchParams.set("chatId", chatId._id);

        // Add botId if chat has a model
        if (chatId.modelHandle?._id) {
          newSearchParams.set("botId", chatId.modelHandle._id);
        }

        navigate(`/dashboard/${chatId._id}?${newSearchParams.toString()}`, {
          replace: true,
        });
      } else {
        // Clear chatId from query params but keep botId if it exists
        newSearchParams.delete("chatId");
        navigate(`/dashboard?${newSearchParams.toString()}`, { replace: true });
      }

      // Close mobile menu after selection on mobile
      setMobileMenuOpen(false);
      setMobileView("main");
    },
    [navigate, searchParams],
  );

  const handleChatTitleUpdate = useCallback((chatId, newTitle) => {
    setChatTitles((prev) => ({ ...prev, [chatId]: newTitle }));
  }, []);

  // Function to trigger sidebar refresh
  const handleRefreshSidebar = useCallback(() => {
    setRefreshSidebar((prev) => prev + 1);
  }, []);

  // Memoize CentreArea props to prevent unnecessary re-renders
  const centreAreaProps = useMemo(
    () => ({
      fetching,
      messages,
      setMessages,
      allCustomModels,
      customModelId,
      selectedChatId,
      setCustomModelId: handleManualModelChangeCenterArea,
      setSelectedChatId,
      onNewChat: () => {
        setMessages([]);
        setIsNewChat(true);
        setSelectedChatId(null);
        navigate("/dashboard", { replace: true });
      },
      setChats,
      setAllChats,
      chats,
      allChats,
      isNewChat,
      setIsNewChat,
      onRefreshSidebar: handleRefreshSidebar,
      onChatTitleUpdate: handleChatTitleUpdate,
      storyComplete: selectedChatCompleted,
      selectedModel,
      onChatSelect: handleChatSelect,
      onStreamingChange: setIsChatStreaming,
    }),
    [
      fetching,
      messages,
      allCustomModels,
      customModelId,
      selectedChatId,
      chats,
      allChats,
      isNewChat,
      setIsNewChat,
      selectedChatCompleted,
      selectedModel,
      handleRefreshSidebar,
      handleChatTitleUpdate,
      handleManualModelChangeCenterArea,
      handleChatSelect,
      navigate,
    ],
  );

  // Memoize LeftSidebar props
  const leftSidebarProps = useMemo(
    () => ({
      chats,
      setChats,
      setAllChats,
      selectedChatId,
      onChatSelect: handleChatSelect,
      refreshTrigger: refreshSidebar,
      onNewChat: () => {
        setMessages([]);
        setIsNewChat(true);
        setSelectedChatId(null);
        navigate("/dashboard", { replace: true });
      },
      onRefreshConversations: fetchConversations,
      chatTitles,
      setIsModalOpen,
      setModalMessage,
      setShowContactAdmin,
      setModalTitle,
      setTierCode,
      allCustomModels,
      customModelId,
      setSelectedChatId,
      setIsNewChat,
      chatId: selectedChatId,

      onClose: () => setMobileView("main"),
      onModelSelect: handleManualModelChange,
      setMessages: setMessages,
      clientBadgeLabel:
        isAuthenticated && userData ? sessionClientBadgeLabel : null,
    }),
    [
      chats,
      setAllChats,
      selectedChatId,
      refreshSidebar,
      chatTitles,
      allCustomModels,
      customModelId,
      handleChatSelect,
      fetchConversations,
      handleManualModelChange,
      navigate,
      setMessages,
      isAuthenticated,
      userData,
      sessionClientBadgeLabel,
    ],
  );

  // Toggle mobile sidebar view
  const toggleMobileView = (view) => {
    if (mobileView === view) {
      setMobileView("main");
    } else {
      setMobileView(view);
    }
  };

  return (
    <div className="w-screen h-screen overflow-hidden backdrop-blur-sm">
      {/* Mobile Header */}
      <div className="lg:hidden flex items-center justify-between px-3 py-2.5 bg-white/90 border-b border-gray-200 fixed top-0 left-0 right-0 z-30 shadow-sm">
        <button
          onClick={() => toggleMobileView("left")}
          className="p-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 cursor-pointer active:scale-95 transition-transform"
          aria-label="Open menu"
        >
          <Menu size={20} className="text-gray-800" />
        </button>
        <h1 className="text-gray-800 font-medium text-sm sm:text-base">
          Sandbox Agents
        </h1>
        <button
          onClick={() =>
            window.open("https://aidistrictagents.com/profile", "_blank")
          }
          className="p-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 cursor-pointer active:scale-95 transition-transform"
          aria-label="Open profile"
        >
          <User2 size={20} className="text-gray-800" />
        </button>
      </div>

      {isAuthenticated && userData && clientSessionMismatch && (
        <div
          className={`fixed z-[35] max-w-[min(100vw-2rem,24rem)] transition-[transform,opacity] duration-200 ${
            clientMismatchPopupMinimized
              ? "top-3 right-4 max-w-sm"
              : "top-3 right-4"
          }  lg:right-4 lg:max-w-md`}
          role="complementary"
          aria-label="Portal client mismatch"
        >
          {clientMismatchPopupMinimized ? (
            <button
              type="button"
              onClick={() => setClientMismatchPopupMinimized(false)}
              className="flex w-full items-center justify-center gap-2 rounded-full border border-[#ffe58f] bg-[#fffbe6] px-4 py-2.5 text-left text-xs font-semibold text-yellow-900 shadow-lg hover:bg-amber-50 sm:inline-flex sm:w-auto"
            >
              <span
                className="h-2 w-2 shrink-0 rounded-full bg-amber-500"
                aria-hidden
              />
              Portal client differs — tap to expand
              <ChevronUp className="h-4 w-4 shrink-0 opacity-70" aria-hidden />
            </button>
          ) : (
            <div className="overflow-hidden rounded-xl border border-[#ffe58f] bg-[#fffbe6] shadow-xl">
              <div className="flex items-center justify-between gap-2 border-b border-[#ffe58f] bg-[#fff7d6] px-3 py-2">
                <span className="text-xs font-semibold text-yellow-950 sm:text-sm">
                  Portal client mismatch
                </span>
                <button
                  type="button"
                  onClick={() => setClientMismatchPopupMinimized(true)}
                  className="inline-flex shrink-0 items-center gap-1 rounded-md border border-yellow-600/30 bg-white/80 px-2 py-1 text-xs font-medium text-yellow-900 hover:bg-white"
                  aria-label="Minimize client mismatch notice"
                >
                  <ChevronDown className="h-4 w-4" aria-hidden />
                </button>
              </div>
              <div
                role="status"
                aria-live="polite"
                className="flex flex-col gap-4 px-3 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-6"
              >
                <div className="text-xs leading-relaxed text-yellow-900 sm:text-sm">
                  <p>
                    You&apos;ve selected a different client in the portal. Would
                    you like to switch to{" "}
                    <span className="font-bold">{portalClientLabel}</span>?
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleSwitchToPortalClient}
                  disabled={isSwitchingClient || isChatStreaming}
                  className="shrink-0 rounded-md cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 border border-yellow-500 bg-yellow-100 px-3 py-1 text-sm font-medium text-yellow-800 hover:bg-yellow-200"
                >
                  {isSwitchingClient ? "Switching…" : "Switch Client"}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="flex flex-col lg:flex-row gap-2 sm:gap-3 lg:gap-4 p-2 sm:p-3 lg:p-4 h-[calc(100vh-48px)] lg:h-screen pt-12 sm:pt-14 lg:pt-4">
        {/* Left Sidebar - Hidden on mobile unless toggled */}
        <AnimatePresence>
          {(mobileView === "left" || windowWidth >= 1024) && (
            <motion.div
              className={`${
                mobileView === "left"
                  ? "fixed inset-0 z-40 p-2 sm:p-3 pt-12 sm:pt-14"
                  : "w-full lg:w-[28%] hidden lg:block"
              }`}
              initial={
                mobileView === "left" ? { x: "-100%", opacity: 0 } : false
              }
              animate={mobileView === "left" ? { x: 0, opacity: 1 } : false}
              exit={mobileView === "left" ? { x: "-100%", opacity: 0 } : false}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
            >
              {mobileView === "left" && (
                <>
                  <div
                    className="absolute inset-0 bg-black/20 backdrop-blur-sm"
                    onClick={() => setMobileView("main")}
                  />
                  <button
                    className="absolute top-3 right-2 sm:top-4 sm:right-3 p-2 bg-gray-400/90 hover:bg-gray-500 rounded-full z-50 cursor-pointer shadow-lg active:scale-95 transition-transform"
                    onClick={() => setMobileView("main")}
                    aria-label="Close menu"
                  >
                    <X size={18} className="text-white" />
                  </button>
                </>
              )}
              <div
                className={`${
                  mobileView === "left"
                    ? "relative z-10 h-full overflow-hidden bg-white rounded-xl shadow-xl"
                    : ""
                }`}
              >
                <LeftSidebar ref={sidebarRef} {...leftSidebarProps} />
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Center Area - Always visible but adjusted for mobile */}
        <div
          className={`${
            mobileView === "main" || windowWidth >= 1024 ? "flex" : "hidden"
          } w-full lg:w-[72%] flex-1 min-w-0 mt-6 flex-col lg:mt-0`}
        >
          <div className="flex min-h-0 min-w-0 flex-1 flex-col">
            <CentreArea
              ref={centerRef}
              {...centreAreaProps}
              onNewChatStarted={() => {
                setIsNewChat(true);
              }}
            />
          </div>
        </div>
      </div>
      {isModalOpen && (
        <AlertModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          title={modalTitle}
          message={modalMessage}
          showContactAdmin={showContactAdmin}
          tierCode={tierCode}
        />
      )}
    </div>
  );
};

export default Dashboard;
